import { convexQuery } from "@convex-dev/react-query";
import { formatDistanceToNow } from "@plasma/utils/time";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  Clock,
  Monitor,
  RotateCcw,
  ShieldAlert,
  Wifi,
  WifiOff,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import {
  type KdsDeviceWithStation,
  kdsGetDevicesWithStations,
  kdsTriggerDeviceReset,
} from "~/api-refs";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "~/components/ui/alert-dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Skeleton } from "~/components/ui/skeleton";
import { useEmployeePermissions } from "~/hooks/use-employee-permissions";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";

export const Route = createFileRoute(
  "/_authenticated/locations/$id/kds-devices"
)({
  component: KdsDevicesPage,
});

type KdsDevice = KdsDeviceWithStation;

function KdsDevicesPage() {
  const { id } = Route.useParams();
  const {
    hasPermission,
    isLoading: permissionsLoading,
    hasEmployee,
  } = useEmployeePermissions();
  const canViewIntegrations = hasPermission("viewIntegrations");

  const { data: devices, isPending } = useQuery({
    ...convexQuery(kdsGetDevicesWithStations, {
      locationId: toConvexTableId("locations", id),
    }),
    enabled: canViewIntegrations,
  });

  if (permissionsLoading) {
    return <Skeleton className="h-96 w-full" />;
  }

  if (!hasEmployee) {
    return (
      <Alert>
        <ShieldAlert className="size-4" />
        <AlertTitle>No Employee Profile</AlertTitle>
        <AlertDescription>
          Your account is not linked to an employee profile. Contact your
          organization admin to set up your employee record with the appropriate
          permissions.
        </AlertDescription>
      </Alert>
    );
  }

  if (!canViewIntegrations) {
    return (
      <Alert variant="destructive">
        <ShieldAlert className="size-4" />
        <AlertTitle>Access Denied</AlertTitle>
        <AlertDescription>
          You don't have permission to view devices and integrations. Contact
          your manager to request access.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-6 py-5">
      <div className="space-y-1">
        <h2 className="body text-foreground text-weight-medium">KDS Devices</h2>
        <p className="body-sm text-muted-foreground leading-tight">
          View and manage Kitchen Display System devices at this location
        </p>
      </div>
      {isPending && (
        <div className="space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      )}
      {!isPending && devices && <KdsDevicesList devices={devices} />}
    </div>
  );
}

function KdsDevicesList({ devices }: { devices: KdsDevice[] }) {
  const [query, setQuery] = useState("");

  const filteredDevices = devices.filter(
    (device) =>
      !query || device.name.toLowerCase().includes(query.toLowerCase())
  );

  if (devices.length === 0) {
    return (
      <div className="border-border bg-muted flex flex-col items-center justify-center rounded-xl border border-dashed p-12">
        <Monitor className="text-muted-foreground mb-4 size-16" />
        <h3 className="subtitle-sm text-foreground mb-2">
          No KDS devices registered
        </h3>
        <p className="body-sm text-muted-foreground mb-4 max-w-md text-center">
          KDS devices register automatically when they connect through the KDS
          app. Open the Plasma KDS app on a tablet or browser to get started.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <Input
          className="w-80 rounded-xl"
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search device name..."
          value={query}
        />
        <Badge className="ml-auto" variant="secondary">
          {devices.length} device{devices.length !== 1 ? "s" : ""}
        </Badge>
      </div>
      <div className="space-y-3">
        {filteredDevices.length > 0 ? (
          filteredDevices.map((device) => (
            <KdsDeviceCard device={device} key={device._id} />
          ))
        ) : (
          <div className="border-border bg-muted flex flex-col items-center justify-center rounded-xl border p-8">
            <p className="body-sm text-muted-foreground">
              No devices match &quot;{query}&quot;
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function KdsDeviceCard({ device }: { device: KdsDevice }) {
  const isOnline = device.active && device.hasActiveSession;
  const isPendingReset = !!device.forceResetAt;

  const resetMutation = useMutation({
    mutationFn: () =>
      convexClient.mutation(kdsTriggerDeviceReset, {
        deviceId: toConvexTableId("kdsDevices", device._id),
      }),
    onSuccess: () => {
      toast.success(`Reset command sent to "${device.name}"`);
    },
    onError: () => {
      toast.error(`Failed to reset "${device.name}"`);
    },
  });

  return (
    <div className="border-border flex items-center justify-between rounded-xl border p-4">
      <div className="flex items-center gap-4">
        <div className="bg-muted flex size-10 items-center justify-center rounded-lg">
          <Monitor className="text-muted-foreground size-5" />
        </div>

        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <p className="subtitle-sm text-foreground">{device.name}</p>
            {isPendingReset && (
              <Badge className="text-xs" variant="outline">
                Reset pending
              </Badge>
            )}
          </div>
          <div className="text-muted-foreground flex items-center gap-3">
            {device.stationName ? (
              <span className="body-sm">
                Station:{" "}
                <span className="text-foreground">{device.stationName}</span>
              </span>
            ) : (
              <span className="body-sm italic">No station paired</span>
            )}
            <span className="text-border">|</span>
            <span className="body-sm flex items-center gap-1 tabular-nums">
              <Clock className="size-3" />
              Last seen{" "}
              {formatDistanceToNow(device.lastPing ?? device._creationTime)}
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Badge variant={isOnline ? "success" : "secondary"}>
          <div className="flex items-center gap-1.5">
            {isOnline ? (
              <Wifi className="size-3" />
            ) : (
              <WifiOff className="size-3" />
            )}
            {isOnline ? "Online" : "Offline"}
          </div>
        </Badge>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              aria-label={`Reset ${device.name}`}
              disabled={isPendingReset || resetMutation.isPending}
              size="sm"
              variant="outline"
            >
              <RotateCcw className="mr-1.5 size-3.5" />
              Reset
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reset KDS Device</AlertDialogTitle>
              <AlertDialogDescription>
                This will send a reset command to &quot;{device.name}&quot;. The
                device will clear its local data and return to the setup screen
                on its next heartbeat. The device will need to be reconfigured
                after reset.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => resetMutation.mutate()}>
                Reset Device
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
